/**
 * Service de calcul des réductions
 * Gère le calcul des codes de réduction selon leurs règles spécifiques
 */
class DiscountService {
    constructor() {
        // Le service est un singleton
    }

    /**
     * IDs des codes de réduction avec comportements spéciaux
     */
    static get SPECIAL_DISCOUNT_CODES() {
        return {
            STLYON25: 194,
            STLYON15: 1042,
            LGCHER24: 467,
            VVX26: 2444,
        };
    }

    /**
     * Calcule la réduction à appliquer selon le code de réduction
     * 
     * @param {Object} code_reduction - L'objet Code_Reduction
     * @param {Object} breakdown - Le breakdown du panier avec les prix par type
     * @param {Object} inscription - L'inscription complète (nécessaire pour certains codes spéciaux)
     * @returns {Promise<Object>} Le panier de réduction avec { prix, nb_articles }
     */
    async computeDiscount(code_reduction, breakdown, inscription) {

        // Vérifier que le code de réduction existe
        if (!code_reduction) {
            return { nb_articles: 0, prix: 0 };
        }

        // Extraire les valeurs du breakdown avec valeurs par défaut
        const course_cart = breakdown.courses || { prix: 0, nb_articles: 0 };
        const pack_cart = breakdown.packs_courses || { prix: 0, nb_articles: 0 };
        const options_cart = breakdown.options || { prix: 0, nb_articles: 0 };
        const activites_cart = breakdown.activites || { prix: 0, nb_articles: 0 };

        // Gérer les codes de réduction spéciaux
        const discountId = parseInt(code_reduction.id);

        if (discountId === DiscountService.SPECIAL_DISCOUNT_CODES.STLYON25) {
            return this._computeSTLYON25Discount(code_reduction, course_cart, inscription);
        }

        if (discountId === DiscountService.SPECIAL_DISCOUNT_CODES.STLYON15) {
            return this._computeSTLYON15Discount(code_reduction, course_cart, inscription);
        }

        if (discountId === DiscountService.SPECIAL_DISCOUNT_CODES.LGCHER24) {
            return this._computeLGCHER24Discount(code_reduction, course_cart);
        }
        if (discountId === DiscountService.SPECIAL_DISCOUNT_CODES.VVX26) {
            return this._computeVVX26Discount(code_reduction, course_cart, inscription);
        }

        // Calcul standard pour les autres codes de réduction
        return this._computeStandardDiscount(
            code_reduction,
            course_cart,
            pack_cart,
            options_cart,
            activites_cart
        );
    }

    /**
     * Calcule la réduction STLYON25 (nécessite la traversée Chirats 80KM id=136)
     * 
     * @private
     * @param {Object} code_reduction - Le code de réduction
     * @param {Object} course_cart - Le panier des courses
     * @param {Object} inscription - L'inscription avec list_courses
     * @returns {Object} Le panier de réduction
     */
    _computeSTLYON25Discount(code_reduction, course_cart, inscription) {
        // Vérifier si la traversée Chirats 80KM (id=136) est sélectionnée
        const hasTraverseeChirats = this._hasCourseSelected(inscription, "136");

        if (!hasTraverseeChirats || !course_cart || course_cart.prix === 0) {
            return { nb_articles: 1, prix: 0 };
        }

        return this._applyDiscountToPrice(code_reduction, course_cart.prix);
    }

    _computeVVX26Discount(code_reduction, course_cart, inscription) {
        const hasXGT = this._hasCourseSelected(inscription, "1493") || this._hasCourseSelected(inscription, "1494");
        if (!hasXGT || !course_cart || course_cart.prix === 0) {
            return { nb_articles: 1, prix: 0 };
        }
        return this._applyDiscountToPrice(code_reduction, course_cart.prix);
    }

    /**
     * Calcule la réduction STLYON15 (nécessite la traversée Chirats 80KM id=744)
     * 
     * @private
     * @param {Object} code_reduction - Le code de réduction
     * @param {Object} course_cart - Le panier des courses
     * @param {Object} inscription - L'inscription avec list_courses
     * @returns {Object} Le panier de réduction
     */
    _computeSTLYON15Discount(code_reduction, course_cart, inscription) {
        // Vérifier si la traversée Chirats 80KM (id=744) est sélectionnée
        const hasTraverseeChirats = this._hasCourseSelected(inscription, "744");

        if (!hasTraverseeChirats || !course_cart || course_cart.prix === 0) {
            return { nb_articles: 1, prix: 0 };
        }

        return this._applyDiscountToPrice(code_reduction, course_cart.prix);
    }

    /**
     * Calcule la réduction LGCHER24 (nécessite un minimum de 15€ de courses)
     * 
     * @private
     * @param {Object} code_reduction - Le code de réduction
     * @param {Object} course_cart - Le panier des courses
     * @returns {Object} Le panier de réduction
     */
    _computeLGCHER24Discount(code_reduction, course_cart) {
        const coursePrice = course_cart?.prix || 0;

        // LGCHER24 ne s'applique que si le prix des courses est >= 15€
        // et seulement pour les remises en euros
        if (coursePrice < 15 || code_reduction.type !== "Remise en euros") {
            return { nb_articles: 1, prix: 0 };
        }

        return this._applyDiscountToPrice(code_reduction, coursePrice);
    }

    /**
     * Calcule la réduction standard selon apply_to et type
     * 
     * @private
     * @param {Object} code_reduction - Le code de réduction
     * @param {Object} course_cart - Le panier des courses
     * @param {Object} pack_cart - Le panier des packs
     * @param {Object} options_cart - Le panier des options
     * @param {Object} activites_cart - Le panier des activités
     * @returns {Object} Le panier de réduction
     */
    _computeStandardDiscount(code_reduction, course_cart, pack_cart, options_cart, activites_cart) {
        // Calculer le prix total selon apply_to
        const totalDossardsPrice = (course_cart.prix || 0) + (pack_cart.prix || 0);
        const totalOptionsPrice = (options_cart.prix || 0) + (activites_cart.prix || 0);

        let totalReductionablePrice = 0;

        if (code_reduction.apply_to === "deux") {
            // S'applique à tout (courses + options)
            totalReductionablePrice = totalDossardsPrice + totalOptionsPrice;
        } else if (code_reduction.apply_to === "courses") {
            // S'applique uniquement aux courses et packs
            totalReductionablePrice = totalDossardsPrice;
        } else if (code_reduction.apply_to === "options") {
            // S'applique uniquement aux options et activités
            totalReductionablePrice = totalOptionsPrice;
        }

        return this._applyDiscountToPrice(code_reduction, totalReductionablePrice);
    }

    /**
     * Applique la réduction au prix selon le type (Pourcentage ou Remise en euros)
     * 
     * @private
     * @param {Object} code_reduction - Le code de réduction
     * @param {number} price - Le prix sur lequel appliquer la réduction
     * @returns {Object} Le panier de réduction avec { prix, nb_articles }
     */
    _applyDiscountToPrice(code_reduction, price) {
        const cart = { nb_articles: 1, prix: 0 };

        if (code_reduction.type === "Pourcentage") {
            const discount = price * (parseFloat(code_reduction.valeur) / 100);
            cart.prix = -parseFloat(discount.toFixed(2));
        } else if (code_reduction.type === "Remise en euros") {
            // La réduction ne peut pas dépasser le prix total
            const discountValue = Math.min(parseFloat(code_reduction.valeur), price);
            cart.prix = -parseFloat(discountValue.toFixed(2));
        }

        return cart;
    }

    /**
     * Vérifie si une course spécifique est sélectionnée dans l'inscription
     * 
     * @private
     * @param {Object} inscription - L'inscription avec list_courses
     * @param {string} courseId - L'ID de la course à vérifier
     * @returns {boolean} True si la course est sélectionnée
     */
    _hasCourseSelected(inscription, courseId) {
        if (!inscription || !inscription.list_courses || !inscription.list_courses.list) {
            return false;
        }

        const courses = inscription.list_courses.list;
        for (let i = 0; i < courses.length; i++) {
            if (courses[i].id === courseId && courses[i].selected) {
                return true;
            }
        }

        return false;
    }
}

export default new DiscountService();